import { useCallback, useMemo } from "react";
import {
  Droplet,
  LineChart,
  List,
  ShieldCheck,
  TrendingUp,
  User,
  Users,
} from "lucide-react";
import { splitIntoInsightBuckets } from "@/components/AiInsightsCard";
import { useMistralFirstInsights } from "@/hooks/useMistralFirstInsights";
import { parseInsightBullets } from "@/utils/parseInsightBullets";
import {
  bulletsToActionQueue,
  bulletsToHeroCopy,
  bulletsToInsightCards,
} from "@/utils/mistralInsightMappers";
import {
  buildGenderActionQueue,
  buildGenderComposition,
  buildGenderGapRows,
  buildGenderHeroCopy,
  buildGenderInclusionInsights,
  buildGenderInsightsMarkdown,
  buildGpiGaugeStyle,
  buildGpiTrendChart,
  buildStageParityCards,
  deriveParityBand,
  fmt,
  pct,
} from "@/utils/enrolmentDerived";
import HeroStatusChip from "@/components/HeroStatusChip";
import "./EnrolmentGenderTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const INSIGHT_ICONS = {
  parity: TrendingUp,
  "girls-share": User,
  "gap-class": List,
  inclusion: Droplet,
};

const ACTION_ICONS = {
  orange: LineChart,
  pink: User,
  purple: ShieldCheck,
};

const parityBadgeClass = (band) => {
  if (band === "good") return "enr-gi-badge-good";
  if (band === "watch") return "enr-gi-badge-watch";
  return "enr-gi-badge-risk";
};

const parityTagClass = (band) => {
  if (band === "good") return "good";
  if (band === "watch") return "watch";
  return "risk";
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function EnrolmentGenderTab({ overview = {}, classData = [], stageData = [] }) {
  const gpi = overview.gender_parity_index ?? 0;
  const girlsPct = overview.girls_participation_pct ?? 0;
  const boysPct = clampPct(100 - girlsPct);

  const buildLocalMarkdown = useCallback(
    () => buildGenderInsightsMarkdown(overview, classData),
    [overview, classData]
  );

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        gpi: overview.gender_parity_index,
        girls: overview.girls_participation_pct,
        classes: classData.length,
      }),
    [overview.gender_parity_index, overview.girls_participation_pct, classData.length]
  );

  const { markdown: aiMarkdown, loading: insightsLoading } = useMistralFirstInsights({
    kind: "enrolment-gender",
    title: "Gender Inclusion Insights",
    buildLocalMarkdown,
    context: { overview, classData, stageData },
    enabled: Boolean(overview?.total_schools),
    scopeKey,
  });

  const buckets = useMemo(() => splitIntoInsightBuckets(aiMarkdown), [aiMarkdown]);

  const localHero = useMemo(() => buildGenderHeroCopy(overview, classData), [overview, classData]);
  const hero = useMemo(() => {
    if (insightsLoading) return { ...localHero, title: "Generating AI insights…", body: "" };
    const bullets = parseInsightBullets(buckets.insights);
    return bullets.length ? bulletsToHeroCopy(bullets, localHero) : localHero;
  }, [insightsLoading, buckets.insights, localHero]);

  const gaugeStyle = useMemo(() => buildGpiGaugeStyle(gpi), [gpi]);
  const localInsights = useMemo(
    () => buildGenderInclusionInsights(overview, classData),
    [overview, classData]
  );
  const insights = useMemo(() => {
    if (insightsLoading) return [];
    const aiInsights = bulletsToInsightCards(parseInsightBullets(buckets.root), localInsights, [
      "parity",
      "girls-share",
      "gap-class",
      "inclusion",
    ]);
    return aiInsights?.length ? aiInsights : localInsights;
  }, [insightsLoading, buckets.root, localInsights]);
  const composition = useMemo(() => buildGenderComposition(overview), [overview]);
  const stageCards = useMemo(() => buildStageParityCards(stageData), [stageData]);
  const gpiChart = useMemo(() => buildGpiTrendChart(classData), [classData]);
  const gapRows = useMemo(() => buildGenderGapRows(classData), [classData]);
  const localActions = useMemo(() => buildGenderActionQueue(overview), [overview]);
  const actions = useMemo(() => {
    if (insightsLoading) return [];
    const aiActions = bulletsToActionQueue(parseInsightBullets(buckets.actions), localActions);
    return aiActions?.length ? aiActions : localActions;
  }, [insightsLoading, buckets.actions, localActions]);

  const heatmapRows = useMemo(
    () =>
      classData.map((row) => {
        const parity = deriveParityBand(row.gpi ?? 0);
        return { ...row, parity };
      }),
    [classData]
  );

  const tableRows = useMemo(
    () =>
      classData.map((row) => {
        const parity = deriveParityBand(row.gpi ?? 0);
        const gap = Math.max(0, (row.boys ?? 0) - (row.girls ?? 0));
        return { ...row, parity, gap };
      }),
    [classData]
  );

  const watchZoneLabel = useMemo(() => {
    const classNums = classData
      .filter((c) => (c.gpi ?? 1) <= 0.87)
      .map((c) => {
        const match = c.class_name?.match(/^Class (\d+)$/);
        return match ? Number(match[1]) : null;
      })
      .filter((n) => n != null)
      .sort((a, b) => a - b);
    if (classNums.length >= 2) {
      return `Class ${classNums[0]}–${classNums[classNums.length - 1]} watch zone`;
    }
    if (classNums.length === 1) return `Class ${classNums[0]} watch zone`;
    return "Upper-primary watch zone";
  }, [classData]);

  const targetBandY = 64;
  const targetBandHeight = 68;

  return (
    <div className="enr-gi-tab">
      <section className="enr-gi-hero">
        <div className="enr-gi-hero-layout">
          <div>
            <div className="enr-gi-parity-main">
              <div className="enr-gi-gauge" style={gaugeStyle} aria-label={`Gender Parity Index ${gpi}`}>
                <div>
                  <strong>{gpi.toFixed(2)}</strong>
                  <span>GPI</span>
                </div>
              </div>
              <div className="enr-gi-hero-copy">
                <h2>{toTitleCase(hero.title)}</h2>
                <p>{hero.body}</p>
                <div className="enr-gi-status-row">
                  {hero.belowTarget ? (
                    <HeroStatusChip
                      chipClass="enr-gi-status-chip"
                      onClick={() => scrollToId("enr-gi-class-risk")}
                      ariaLabel="Below target band — view class-wise GPI risk"
                    >
                      Below target band
                    </HeroStatusChip>
                  ) : null}
                  <HeroStatusChip chipClass="enr-gi-status-chip" variant="blue" showChevron={false}>
                    Target 0.95–1.05
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="enr-gi-status-chip"
                    variant="pink"
                    onClick={() => scrollToId("enr-gi-class-risk")}
                    ariaLabel={`${watchZoneLabel} — view class-wise gender parity risk`}
                  >
                    {watchZoneLabel}
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="enr-gi-summary-note">
              <div className="enr-gi-icon-box pink">
                <Users size={22} />
              </div>
              <div>
                <b>Primary action</b>
                <span>{hero.primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="enr-gi-kpi-grid" aria-label="Gender and inclusion KPIs">
            <article className="enr-gi-kpi-card">
              <small>Total Boys</small>
              <strong className="blue-text">{fmt(overview.total_boys)}</strong>
              <span>{pct(boysPct)} of total enrolment</span>
            </article>
            <article className="enr-gi-kpi-card pink">
              <small>Total Girls</small>
              <strong className="pink-text">{fmt(overview.total_girls)}</strong>
              <span>{pct(girlsPct)} of total enrolment</span>
            </article>
            <article className="enr-gi-kpi-card green">
              <small>Gender Parity Index</small>
              <strong className="green-text">{gpi.toFixed(2)}</strong>
              <span>Target range 0.95–1.05</span>
            </article>
            <article className="enr-gi-kpi-card orange">
              <small>Gender Gap</small>
              <strong className="orange-text">{fmt(overview.gender_gap)}</strong>
              <span>Boys minus girls</span>
            </article>
          </div>
        </div>
      </section>

      <div className="enr-gi-section-title">
        <div>
          <h2>Executive Inclusion Snapshot</h2>
          <p>
            The first scan now tells users what is happening, where parity is weak, and which classes need
            immediate follow-up.
          </p>
        </div>
        <button type="button" className="enr-gi-section-action" onClick={() => scrollToId("enr-gi-class-risk")}>
          View class risk →
        </button>
      </div>

      <section className="enr-gi-insight-grid">
        {insights.map((item) => {
          const Icon = INSIGHT_ICONS[item.id] || TrendingUp;
          return (
            <article key={item.id} className="enr-gi-insight-card">
              <div className={`enr-gi-icon-box ${item.iconClass || ""}`.trim()}>
                <Icon size={22} />
              </div>
              <h3>{toTitleCase(item.title)}</h3>
              <p>{item.body}</p>
              <div className="enr-gi-mini-stat">
                {item.stat} <small>{item.statSuffix}</small>
              </div>
            </article>
          );
        })}
      </section>

      <div className="enr-gi-section-title">
        <div>
          <h2>Gender Composition and Stage Parity</h2>
          <p>
            Composition and stage-level GPI are separated from detailed class charts so the dashboard feels
            less crowded and easier to interpret.
          </p>
        </div>
      </div>

      <section className="enr-gi-analytics-grid">
        <article className="enr-gi-panel">
          <div className="enr-gi-panel-head">
            <div>
              <h3>Overall Gender Distribution</h3>
              <p>Distribution of enrolment by recorded gender.</p>
            </div>
            <span className="enr-gi-pill">{fmt(overview.grand_total)} total</span>
          </div>
          <div className="enr-gi-composition-wrap">
            <div className="enr-gi-donut" style={composition.donutStyle} aria-label="Gender distribution donut">
              <div className="enr-gi-donut-center">
                <div>
                  <strong>{pct(composition.girlsShare)}</strong>
                  <span>Girls share</span>
                </div>
              </div>
            </div>
            <div className="enr-gi-legend-list">
              <div className="enr-gi-legend-item">
                <div className="enr-gi-legend-row">
                  <span>
                    <i className="enr-gi-dot" />
                    Boys
                  </span>
                  <b>
                    {fmt(composition.boys)} · {pct(composition.boysPct)}
                  </b>
                </div>
                <div className="enr-gi-track">
                  <div className="enr-gi-fill" style={{ width: `${composition.boysPct}%` }} />
                </div>
              </div>
              <div className="enr-gi-legend-item">
                <div className="enr-gi-legend-row">
                  <span>
                    <i className="enr-gi-dot pink" />
                    Girls
                  </span>
                  <b>
                    {fmt(composition.girls)} · {pct(composition.girlsPct)}
                  </b>
                </div>
                <div className="enr-gi-track">
                  <div className="enr-gi-fill pink" style={{ width: `${composition.girlsPct}%` }} />
                </div>
              </div>
              <div className="enr-gi-legend-item">
                <div className="enr-gi-legend-row">
                  <span>
                    <i className="enr-gi-dot purple" />
                    Transgender
                  </span>
                  <b>{composition.transLabel}</b>
                </div>
                <div className="enr-gi-track">
                  <div
                    className="enr-gi-fill purple"
                    style={{ width: `${composition.transBarWidth}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </article>

        <article className="enr-gi-panel">
          <div className="enr-gi-panel-head">
            <div>
              <h3>Stage-Wise Parity Health</h3>
              <p>Stage-level view helps identify whether parity weakens during progression.</p>
            </div>
            <span className="enr-gi-pill warn">Target 0.95–1.05</span>
          </div>
          <div className="enr-gi-stage-grid">
            {stageCards.map((stage) => (
              <div key={stage.key} className={`enr-gi-stage-card ${stage.tone}`}>
                <b>{stage.label}</b>
                <strong>{stage.gpi.toFixed(2)}</strong>
                <span>{stage.status}</span>
                <div className="enr-gi-stage-meter">
                  <i style={{ width: `${stage.meterWidth}%` }} />
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="enr-gi-section-title" id="enr-gi-class-risk">
        <div>
          <h2>Class-Wise Parity Diagnostics</h2>
          <p>
            The line chart keeps the parity target visible, while the gap chart shows where operational
            follow-up should begin.
          </p>
        </div>
      </div>

      <section className="enr-gi-analytics-grid">
        <article className="enr-gi-panel">
          <div className="enr-gi-panel-head">
            <div>
              <h3>Gender Parity Index by Class</h3>
              <p>Target band is highlighted between 0.95 and 1.05.</p>
            </div>
            <span className="enr-gi-pill">GPI trend</span>
          </div>
          <svg
            className="enr-gi-chart-svg"
            viewBox="0 0 720 330"
            role="img"
            aria-label="Gender parity index by class line chart"
          >
            <rect x="64" y={targetBandY} width="614" height={targetBandHeight} className="enr-gi-target-band" rx="8" />
            <line x1="64" y1="32" x2="64" y2="268" className="enr-gi-axis" />
            <line x1="64" y1="268" x2="678" y2="268" className="enr-gi-axis" />
            <line x1="64" y1="64" x2="678" y2="64" className="enr-gi-grid" />
            <line x1="64" y1="128" x2="678" y2="128" className="enr-gi-grid" />
            <line x1="64" y1="192" x2="678" y2="192" className="enr-gi-grid" />
            <text x="28" y="68" className="enr-gi-axis-label">
              1.05
            </text>
            <text x="28" y="132" className="enr-gi-axis-label">
              0.95
            </text>
            <text x="34" y="196" className="enr-gi-axis-label">
              0.85
            </text>
            <text x="34" y="272" className="enr-gi-axis-label">
              0.75
            </text>
            {gpiChart.path && <path d={gpiChart.path} className="enr-gi-line" />}
            {gpiChart.points.map((p) => (
              <circle
                key={p.class_name}
                cx={p.x}
                cy={p.y}
                r="5"
                className={`enr-gi-point ${p.isRisk ? "enr-gi-risk-point" : ""}`}
              />
            ))}
            {gpiChart.points.map((p) => (
              <text key={`${p.class_name}-label`} x={p.x} y="300" className="enr-gi-axis-label" textAnchor="middle">
                {p.shortLabel}
              </text>
            ))}
            {gpiChart.lowest && gpiChart.lowestPoint && (
              <text x={gpiChart.lowestPoint.x - 48} y={gpiChart.lowestPoint.y + 23} className="enr-gi-risk-note">
                Lowest parity: {gpiChart.lowest.class_name} · {(gpiChart.lowest.gpi ?? 0).toFixed(2)}
              </text>
            )}
          </svg>
        </article>

        <article className="enr-gi-panel">
          <div className="enr-gi-panel-head">
            <div>
              <h3>Largest Gender Gaps by Class</h3>
              <p>Absolute difference between boys and girls, prioritized by size.</p>
            </div>
            <span className="enr-gi-pill warn">Boys - Girls</span>
          </div>
          <div className="enr-gi-gap-chart">
            {gapRows.map((row) => (
              <div key={row.class_name} className={`enr-gi-gap-row ${row.tone || ""}`.trim()}>
                <div className="enr-gi-gap-label">{row.class_name}</div>
                <div className="enr-gi-gap-track">
                  <div className="enr-gi-gap-bar" style={{ width: `${row.barWidth}%` }} />
                </div>
                <div className="enr-gi-gap-value">{fmt(row.gap)}</div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="enr-gi-section-title">
        <div>
          <h2>Parity Heatmap and Action Queue</h2>
          <p>
            Classes are grouped into status bands so field users can quickly move from diagnosis to
            intervention.
          </p>
        </div>
        <button type="button" className="enr-gi-section-action" onClick={() => scrollToId("enr-gi-table")}>
          View detailed table ↓
        </button>
      </div>

      <section className="enr-gi-two-column">
        <article className="enr-gi-panel">
          <div className="enr-gi-panel-head">
            <div>
              <h3>Class Parity Heatmap</h3>
              <p>Green is within target, amber is near target, red requires focused monitoring.</p>
            </div>
            <span className="enr-gi-pill">{classData.length} classes</span>
          </div>
          <div className="enr-gi-heatmap">
            {heatmapRows.map((row) => (
              <div key={row.class_name} className="enr-gi-heat-tile">
                <b>{row.class_name}</b>
                <strong>{(row.gpi ?? 0).toFixed(2)}</strong>
                <span className={parityBadgeClass(row.parity.band)}>{row.parity.heatLabel}</span>
              </div>
            ))}
          </div>
        </article>

        <article className="enr-gi-panel">
          <div className="enr-gi-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Focused next steps for district and block-level review.</p>
            </div>
            <span className="enr-gi-pill warn">Priority</span>
          </div>
          <div className="enr-gi-action-card">
            {actions.map((action) => {
              const Icon = ACTION_ICONS[action.iconClass] || LineChart;
              return (
                <div key={action.title} className="enr-gi-action-item">
                  <div className={`enr-gi-icon-box ${action.iconClass}`}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{action.title}</h4>
                    <p>{action.body}</p>
                  </div>
                </div>
              );
            })}
            <div className="enr-gi-action-footer">
              <b>Next review recommendation</b>
              <span>
                Run a block-wise drilldown for Classes 8–10 and generate school-level parity exception lists
                for field officers.
              </span>
            </div>
          </div>
        </article>
      </section>

      <section className="enr-gi-panel enr-gi-table-card" id="enr-gi-table">
        <div className="enr-gi-table-head">
          <div>
            <h3>Class-Wise Detailed Gender Breakdown</h3>
            <p>
              Detailed view retained for analysts, but moved below executive visuals to reduce first-fold
              clutter.
            </p>
          </div>
          <span className="enr-gi-pill">Boys · Girls · GPI</span>
        </div>
        <table className="enr-gi-table" aria-label="Class-wise gender and GPI detailed table">
          <thead>
            <tr>
              <th>Class</th>
              <th>Boys</th>
              <th>Girls</th>
              <th>Total</th>
              <th>Girls %</th>
              <th>GPI</th>
              <th>Gap</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row) => (
              <tr key={row.class_name}>
                <td>{row.class_name}</td>
                <td>{fmt(row.boys)}</td>
                <td>{fmt(row.girls)}</td>
                <td>{fmt(row.total)}</td>
                <td>{pct(row.girls_pct ?? row.girlsPct ?? 0)}</td>
                <td className="score">{(row.gpi ?? 0).toFixed(2)}</td>
                <td>{fmt(row.gap)}</td>
                <td>
                  <span className={`enr-gi-tag ${parityTagClass(row.parity.band)}`}>
                    {row.parity.tableLabel}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function clampPct(v) {
  return Math.min(100, Math.max(0, Number(v) || 0));
}
